import * as React from "react";
import DataProvider from "../../dataprovider/DataProvider";
import { loadGapiInsideDOM } from "gapi-script";

import { MicrosoftComponent } from "./MicrosoftComponent";
import "bootstrap/dist/css/bootstrap.min.css";

import "./sLoginComponent.scss";
import GoogleLoginComponent from "./GoogleComponent";
import { ICustomAuthComponent as CustomAuthComponent } from "./CustomAuthComponent";
import { mergeStyles } from "@uifabric/merge-styles";
import { Callout, DefaultButton, Dialog, DialogType, DirectionalHint, Icon, Stack, Text } from "office-ui-fabric-react";

require("./LoginComponent.css");

export enum AccountType {
  Microsoft = "Microsoft",
  Google = "Google",
  Custom = "Custom",
}

export interface ILoginComponentProps {
  setAuthStatus: (signedIn: boolean, user: IUserAccount, redirectAfterLogout?: boolean) => Promise<void>;
  currentUser: IAuthenticatedUser;
  hideUnauthenticatedControls?: boolean;
  hideAuthenticatedControls?: boolean;
  headerAccountOnly?: boolean;
}

export interface ILoginComponentState {
  loggedInWithGoogle: boolean;
  loggedInWithMicrosoft: boolean;
  loggedInWithCustomAccount: boolean;
  currentUser: IAuthenticatedUser;

  isCalloutOpen: boolean;
  isLoggedIn: boolean;
  isCustomPanelOpen: boolean;

  error_provider: string;
  isErrorOpen: boolean;
}

// Add the endpoints here for Microsoft Graph API services you'd like to use.
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

export class LoginComponent extends React.Component<ILoginComponentProps, ILoginComponentState> {
  private dataProvider = new DataProvider();
  private refPersonIcon: React.RefObject<Element> = null;

  constructor(props: ILoginComponentProps) {
    super(props);

    this.state = {
      isLoggedIn: Boolean(props.currentUser),
      currentUser: props.currentUser,
      loggedInWithGoogle: props.currentUser && props.currentUser.accountType == AccountType.Google,
      loggedInWithMicrosoft: props.currentUser && props.currentUser.accountType == AccountType.Microsoft,
      loggedInWithCustomAccount: props.currentUser && props.currentUser.accountType == AccountType.Custom,

      isCalloutOpen: false,

      isCustomPanelOpen: false,

      error_provider: null,
      isErrorOpen: false,
    };

    this.refPersonIcon = React.createRef<Element>();
  }
  public componentDidMount() {}

  public ensureSPUser = (userInformation: IUserAccount, token: string, isRSMToken: boolean): Promise<IAuthenticatedUser> => {
    return this.dataProvider
      .ensureExternalUserAccount(userInformation, token, isRSMToken)
      .then((result: IAuthenticatedUser) => {
        console.log(result);
        window.sessionStorage.authToken = JSON.stringify(result.bearerToken);
        window.sessionStorage.stlUser = JSON.stringify(result);

        return result;
      })
      .catch((err) => {
        console.dir(err);
        window.sessionStorage.removeItem("authToken");
        window.sessionStorage.removeItem("stlUser");
        return Promise.reject(err);
      });
  };

  private showAuthenticationError = (provider: string): void => {
    this.setState({
      error_provider: provider,
      isErrorOpen: true,
    });
  };

  public UNSAFE_componentWillReceiveProps(newProps: ILoginComponentProps) {
    if (this.state.currentUser != newProps.currentUser) {
      this.setState({
        isLoggedIn: Boolean(newProps.currentUser),
        currentUser: newProps.currentUser,
        loggedInWithGoogle: newProps.currentUser && newProps.currentUser.accountType == AccountType.Google,
        loggedInWithMicrosoft: newProps.currentUser && newProps.currentUser.accountType == AccountType.Microsoft,
        loggedInWithCustomAccount: newProps.currentUser && newProps.currentUser.accountType == AccountType.Custom,
      });
    }
  }

  private dismissCallout = (): void => {
    this.setState({ isCalloutOpen: false });
  };

  private getAccountProviderLabel = (): string => {
    switch (this.state.currentUser?.accountType) {
      case AccountType.Google:
        return "Google account";
      case AccountType.Microsoft:
        return "Microsoft account";
      case AccountType.Custom:
        return "Company account";
      default:
        return "Account";
    }
  };

  private getAccountDisplayName = (): string => {
    const firstName = this.state.currentUser?.firstName || "";
    const lastName = this.state.currentUser?.lastName || "";
    const displayName = `${firstName} ${lastName}`.trim();

    return displayName || this.state.currentUser?.email || "Signed in user";
  };

  private resetLocalAuthState = (callback?: () => void): void => {
    this.setState(
      {
        isLoggedIn: false,
        currentUser: null,
        loggedInWithGoogle: false,
        loggedInWithMicrosoft: false,
        loggedInWithCustomAccount: false,
        isCalloutOpen: false,
      },
      callback,
    );
  };

  private performAppLogout = async (): Promise<void> => {
    window.sessionStorage.removeItem("authToken");
    window.sessionStorage.removeItem("stlUser");

    await new Promise<void>((resolve) => {
      this.resetLocalAuthState(() => {
        void this.props.setAuthStatus(false, null, false).then(resolve);
      });
    });

    window.location.assign(`${window.location.protocol}//${window.location.host}`);
  };

  private handleLogout = (): void => {
    const accountType = this.state.currentUser?.accountType;
    this.dismissCallout();

    if (accountType === AccountType.Google) {
      loadGapiInsideDOM()
        .then((gapi) => {
          gapi.load("auth2", () => {
            try {
              const auth2 = gapi.auth2?.getAuthInstance?.();

              if (auth2 && typeof auth2.signOut === "function") {
                Promise.resolve(auth2.signOut()).finally(() => {
                  this.performAppLogout();
                });
                return;
              }
            } catch (error) {
              console.error("Google logout failed", error);
            }

            this.performAppLogout();
          });
        })
        .catch(() => {
          this.performAppLogout();
        });
      return;
    }

    if (accountType === AccountType.Microsoft) {
      window.sessionStorage.removeItem("authToken");
      window.sessionStorage.removeItem("stlUser");
      this.resetLocalAuthState(async () => {
        await this.props.setAuthStatus(false, null, false);
        window.location.assign(
          `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(`${window.location.protocol}//${window.location.host}`)}`,
        );
      });
      return;
    }

    this.performAppLogout();
  };

  private renderAccountCallout = (): React.ReactNode => {
    if (!this.state.isCalloutOpen || !this.state.currentUser) {
      return null;
    }

    return (
      <Callout
        className="accountMenuCallout"
        target="#idPersonIcon"
        directionalHint={DirectionalHint.bottomRightEdge}
        gapSpace={10}
        setInitialFocus={true}
        onDismiss={this.dismissCallout}
      >
        <Stack className="accountMenuCard" tokens={{ childrenGap: 10 }}>
          <Text className="accountMenuEyebrow">Signed In</Text>
          <Text className="accountMenuTitle">{this.getAccountDisplayName()}</Text>
          <Text className="accountMenuMeta">{this.state.currentUser.email}</Text>
          <Text className="accountMenuMeta">{this.getAccountProviderLabel()}</Text>
          <DefaultButton className="accountMenuLogoutButton" onClick={this.handleLogout} style={{ color: "#ffffff !important" }}>
            Logout
          </DefaultButton>
        </Stack>
      </Callout>
    );
  };

  public render(): React.ReactElement<ILoginComponentProps> {
    // hide the whole login component if the user is on the pages: 'confirmation', 'resetPW'
    let curUrl = window.location.href.toLowerCase();
    let hideLoginComponent = curUrl.includes("confirm") || curUrl.includes("resetpw") || curUrl.includes("consent") || curUrl.includes("submitagreement");
    console.log(hideLoginComponent);
    const isAuthenticated = this.state.loggedInWithCustomAccount || this.state.loggedInWithGoogle || this.state.loggedInWithMicrosoft;
    const shouldShowUnauthenticatedControls =
      !hideLoginComponent && !this.props.headerAccountOnly && !isAuthenticated && !this.props.hideUnauthenticatedControls;
    const shouldShowAuthenticatedControls = !hideLoginComponent && !this.props.hideAuthenticatedControls && isAuthenticated;
    const signedInStyles = mergeStyles(
      this.props.headerAccountOnly
        ? [
            {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              minHeight: "40px",
            },
          ]
        : hideLoginComponent
          ? [{ display: "none" }]
          : [
              { height: "calc(100% - 100px)" },
              {
                position: "absolute",
                right: "20px",
              },
            ],
    );
    const authenticatedControlStyles = this.props.headerAccountOnly
      ? { display: "flex", alignItems: "center" }
      : { padding: "10px", position: "fixed" as const, top: "64px", right: "10px", zIndex: 6 };

    const { isCustomPanelOpen } = this.state;

    return (
      <div className={signedInStyles}>
        {shouldShowUnauthenticatedControls && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <div id="gSignInWrapper">
              {!this.state.loggedInWithMicrosoft && !this.state.loggedInWithCustomAccount && !isCustomPanelOpen ? (
                <GoogleLoginComponent
                  currentUser={this.state.currentUser}
                  callback={this.ensureSPUser}
                  showError={this.showAuthenticationError}
                  setAuthStatus={(loggedIn: boolean, user: IAuthenticatedUser) => {
                    //console.log("GoogleLogin: ", loggedIn);
                    //console.log("Google account", user);
                    return new Promise((resolve, reject) => {
                      this.setState(
                        (prevState) => {
                          let isLoggedIn = prevState.loggedInWithCustomAccount || loggedIn || prevState.loggedInWithMicrosoft;
                          return {
                            loggedInWithGoogle: loggedIn,
                            loggedInWithCustomAccount: false,
                            loggedInWithMicrosoft: false,
                            isLoggedIn: isLoggedIn,
                            currentUser: user,
                            isCalloutOpen: prevState.isCalloutOpen && loggedIn,
                          };
                        },
                        () => {
                          this.props.setAuthStatus(this.state.isLoggedIn, user).then(() => {
                            resolve();
                          });
                        },
                      );
                    });
                  }}
                />
              ) : (
                ""
              )}
            </div>
            <div id="mSignInWrapper">
              {!this.state.loggedInWithGoogle && !this.state.loggedInWithCustomAccount && !isCustomPanelOpen ? (
                <MicrosoftComponent
                  signedIn={this.state.loggedInWithMicrosoft}
                  currentUser={this.state.currentUser}
                  callback={this.ensureSPUser}
                  showError={this.showAuthenticationError}
                  setAuthStatus={(loggedIn: boolean, user: IAuthenticatedUser) => {
                    //console.log("%cMicrosoftLogin: " + loggedIn, "color:purple;font-size:14px;");
                    return new Promise((resolve, reject) => {
                      this.setState(
                        (prevState: ILoginComponentState, props: ILoginComponentProps) => {
                          let isLoggedIn = prevState.loggedInWithCustomAccount || prevState.loggedInWithGoogle || loggedIn;
                          return {
                            loggedInWithMicrosoft: loggedIn,
                            loggedInWithCustomAccount: false,
                            loggedInWithGoogle: false,
                            isLoggedIn: isLoggedIn,
                            currentUser: user,
                            isCalloutOpen: prevState.isCalloutOpen && loggedIn,
                          };
                        },
                        () => {
                          this.props.setAuthStatus(this.state.isLoggedIn, user).then(() => {
                            resolve();
                          });
                        },
                      );
                    });
                  }}
                />
              ) : (
                ""
              )}
            </div>
            <div id="cSignInWrapper" style={{ marginTop: "-5px" }}>
              {!this.state.loggedInWithGoogle && !this.state.loggedInWithMicrosoft ? (
                <CustomAuthComponent
                  key={`${this.state.isLoggedIn}`}
                  currentUser={this.state.currentUser}
                  dataProvider={this.dataProvider}
                  setAuthStatus={(loggedIn: boolean, user: IAuthenticatedUser) => {
                    return new Promise((resolve, reject) => {
                      console.log("CustomLogin: " + loggedIn);
                      this.setState(
                        (prevState) => {
                          let isLoggedIn = loggedIn || prevState.loggedInWithGoogle || prevState.loggedInWithMicrosoft;
                          return {
                            loggedInWithCustomAccount: loggedIn,
                            isLoggedIn: isLoggedIn,
                            currentUser: user,
                            isCalloutOpen: prevState.isCalloutOpen && loggedIn,
                          };
                        },
                        () => {
                          this.props.setAuthStatus(this.state.isLoggedIn, user);
                          resolve();
                        },
                      );
                    });
                  }}
                  onPanelStatusChange={(isOpen: boolean) => {
                    this.setState({ isCustomPanelOpen: isOpen });
                  }}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        )}
        {shouldShowAuthenticatedControls && (
          <div style={authenticatedControlStyles}>
            <Icon
              id="idPersonIcon"
              className="accountPersonTrigger"
              iconName="Contact"
              styles={{ root: { cursor: "pointer", boxShadow: "3px 3px 8px 0px #0000004d", width: "38px", height: "38px", fontSize: "20px" } }}
              onClick={() => {
                this.setState((prevState) => {
                  return { isCalloutOpen: !prevState.isCalloutOpen };
                });
              }}
              componentRef={this.refPersonIcon}
            />
            {this.renderAccountCallout()}
          </div>
        )}
        <Dialog
          isOpen={this.state.isErrorOpen}
          dialogContentProps={{
            type: DialogType.normal,
            onDismiss: () => {
              this.setState({ isErrorOpen: false });
            },
          }}
          modalProps={{ isBlocking: true, className: "busyOverlay" }}
        >
          Authentication with {this.state.error_provider} was not possible. The Token provided by {this.state.error_provider} was invalid.
        </Dialog>
      </div>
    );
  }
}

export interface IBaseUserAccount {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  accountType: string;
  userId: string;
  isNewUser?: boolean;
}

export interface IUserAccount extends IBaseUserAccount {
  password?: string;
  activateGuid?: string;
}

export interface IAuthenticatedUser extends IBaseUserAccount {
  bearerToken: ITokenInformation;
}

export interface ITokenInformation {
  token: string;
  expirationUTC: Date;
}
